import { useEffect, useMemo, useState } from "react";
import { useSnackbar } from "notistack";
import { IconButton, Menu, MenuItem } from "@mui/material";
import { AccountTreeOutlined, MoreVertOutlined, VisibilityOutlined } from "@mui/icons-material";
import DataTable from "@accredo/fe-shared/components/DataTable.jsx";
import { useMultiTableData } from "./hooks/useMultiTableData.jsx";
import { useUser } from "@accredo/fe-shared/hooks/user.js";
import { getUserRowsPerPage } from "../../utils/userSettings.js";
import { getDatum } from "@accredo/fe-shared/services/formatService.js";
import SectionTemplateViewerModal from "../../pages/admin/claim-forms/components/SectionTemplateViewerModal.jsx";
import SectionTemplatePropagationModal from "../../pages/admin/claim-forms/components/SectionTemplatePropagationModal.jsx";

export default function ClaimFormSectionTemplatesTable() {
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useUser();
  const [viewingSectionTemplate, setViewingSectionTemplate] = useState(null);
  const [propagatingSectionTemplate, setPropagatingSectionTemplate] = useState(null);
  const [rowMenuState, setRowMenuState] = useState({ anchorEl: null, menuRowId: null });

  const handleMenuOpen = (event, id) => {
    event.stopPropagation();
    setRowMenuState({ anchorEl: event.currentTarget, menuRowId: id });
  };
  const handleMenuClose = (event) => {
    event?.stopPropagation();
    setRowMenuState({ anchorEl: null, menuRowId: null });
  };

  const tableKey = "claim_form_section_templates";
  const storageKey = "v2_table_claim_form_section_templates";
  const defaultRowsPerPage = getUserRowsPerPage(user, 10);

  const tablesConfig = useMemo(
    () => [
      {
        key: tableKey,
        label: "Section-Templates",
        route: "data/claim-form-section-templates",
        responseKey: tableKey,
        pageSize: defaultRowsPerPage,
        storageKey,
        extraParams: {
          sort: [{ field: "name", order: "asc" }],
        },
      },
    ],
    [defaultRowsPerPage, storageKey]
  );

  const { tableStates, updateTableState, fetchData } = useMultiTableData(
    tablesConfig,
    enqueueSnackbar
  );

  const tableState = tableStates[tableKey] || {};
  const rows = tableState.rows || [];

  useEffect(() => {
    fetchData(tableKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const columns = [
    {
      accessorKey: "name",
      header: "Name",
      size: 250,
    },
    {
      accessorKey: "key",
      header: "Key",
      size: 200,
    },
    {
      accessorKey: "updated_at",
      header: "Aktualisiert am",
      size: 150,
      Cell: ({ cell }) => getDatum(cell.getValue()),
    },
    {
      accessorKey: "actions",
      header: "",
      enableSorting: false,
      enableColumnFilter: false,
      enableColumnHiding: false,
      size: 60,
      Cell: ({ row }) => (
        <>
          <IconButton size="small" onClick={(e) => handleMenuOpen(e, row.id)}>
            <MoreVertOutlined fontSize="small" />
          </IconButton>
          <Menu
            anchorEl={rowMenuState.anchorEl}
            open={Boolean(rowMenuState.anchorEl) && rowMenuState.menuRowId === row.id}
            onClose={handleMenuClose}
          >
            <MenuItem
              onClick={(e) => {
                handleMenuClose(e);
                setViewingSectionTemplate(row.original);
              }}
            >
              <VisibilityOutlined fontSize="small" sx={{ mr: 1 }} />
              Vorschau
            </MenuItem>
            <MenuItem
              onClick={(e) => {
                handleMenuClose(e);
                setPropagatingSectionTemplate(row.original);
              }}
            >
              <AccountTreeOutlined fontSize="small" sx={{ mr: 1 }} />
              Betroffene Formulare
            </MenuItem>
          </Menu>
        </>
      ),
    },
  ];

  return (
    <>
      <DataTable
        columns={columns}
        data={rows}
        initialState={{ showGlobalFilter: true }}
        isLoading={tableState.isLoading}
        hasLoaded={tableState.hasLoaded}
        pageIndex={tableState.pageIndex}
        pageSize={tableState.pageSize}
        totalRows={tableState.totalRows}
        onChangeSorting={(sorting) => updateTableState(tableKey, { sorting })}
        onChangePage={(nextOrUpdater) => {
          const base = {
            pageIndex: tableState.pageIndex ?? 0,
            pageSize: tableState.pageSize ?? defaultRowsPerPage,
          };
          const next =
            typeof nextOrUpdater === "function"
              ? nextOrUpdater(base)
              : nextOrUpdater;
          if (!next) {
            return;
          }
          updateTableState(tableKey, {
            pageIndex: next.pageIndex,
            pageSize: next.pageSize,
          });
        }}
        onChangeGlobalFilter={(searchword) =>
          updateTableState(tableKey, { searchword })
        }
        storageKey={storageKey}
        enableHeader
        enableSorting
        enableGlobalFilter={true}
        enableColumnFilters={false}
        enableColumnHiding={true}
      />
      <SectionTemplateViewerModal
        open={!!viewingSectionTemplate}
        onClose={() => setViewingSectionTemplate(null)}
        sectionTemplate={viewingSectionTemplate}
      />
      <SectionTemplatePropagationModal
        open={!!propagatingSectionTemplate}
        onClose={() => setPropagatingSectionTemplate(null)}
        sectionTemplate={propagatingSectionTemplate}
      />
    </>
  );
}
